/**
 *
 * Organizations/ContactClientStatuses
 *
 */

import React, { memo } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'

// Graphql
import useQuery from 'components/UseQuery'
import { gql, useMutation } from '@apollo/client'

// Components
import DeleteButton from 'components/DeleteButton'
import EditInPlace from 'components/EditInPlace'

const GET_STATUSES = gql`
	query ($id: ID) {
		organization(id: $id) {
			id
			contact_client_statuses {
				id
				text
				organization_id
				global_default
				default_for_new_contacts
				has_contacts
			}
		}
	}
`

const CREATE_STATUS = gql`
	mutation createContactClientStatus($text: String, $organization_id: ID) {
		createContactClientStatus(input: { text: $text, organization_id: $organization_id }) {
			contact_client_status {
				id
				text
				organization_id
			}
		}
	}
`

const DELETE_STATUS = gql`
	mutation deleteContactClientStatus($id: ID) {
		deleteContactClientStatus(input: { id: $id }) {
			success
		}
	}
`

function ContactClientStatuses({ organization_id }) {
	// const [adding, setAdding] = useState(false)

	const {
		data: { organization },
		refetch,
		loading,
	} = useQuery(GET_STATUSES, {
		variables: { id: organization_id },
		default_data: {
			organization: {
				contact_client_statuses: [
					{ id: 1, text: 'Contact', global_default: true },
					{ id: 2, text: 'Client', global_default: true },
					{ id: 3, text: 'Previous Client', global_default: true },
					{ id: 4, text: 'Dead End', global_default: true },
				],
			},
		},
	})

	const global_default_statuses = organization?.contact_client_statuses.filter(
		(status) => status.global_default === true,
	)
	const organization_statuses = organization?.contact_client_statuses.filter(
		(status) => status.organization_id === organization_id,
	)

	const [create, creation_status] = useMutation(CREATE_STATUS, {
		onCompleted: refetch,
	})

	const [remove] = useMutation(DELETE_STATUS, {
		onCompleted: refetch,
	})

	return (
		<div style={{ marginTop: 10, opacity: loading ? 0.5 : 1 }}>
			<p className="mb-4">
				Create custom Statuses that appear within every Contact across all Users that belong to the
				Organization. Found within the Contact "Details" box, the Status field appears with a dropdown to assign
				the desired status. The Default Statuses available in LeadJig are Prospect, Client, Previous Client, and
				Dead End. New Contacts typically default to a Status of "Prospect".
			</p>

			<h5>Custom Status options for the Organization:</h5>

			<ul className="list-group mb-4" style={{ width: '350px' }}>
				{organization_statuses?.map((status) => (
					<li key={status.id} className="list-group-item">
						{status.text}
						<div className="place-tr mt-3 mr-3">
							{status.has_contacts ? (
								'In use'
							) : (
								<DeleteButton
									onDelete={() => {
										remove({
											variables: {
												id: status.id,
											},
										})
									}}
									style={{ marginTop: '-4px' }}
								/>
							)}
						</div>
					</li>
				))}

				<li className="list-group-item">
					<EditInPlace
						default_text="Add New Contact Client Status"
						initial_value=""
						onSubmit={(new_status_text) => {
							if (new_status_text !== '') {
								create({
									variables: {
										organization_id,
										text: new_status_text,
									},
								})
							}
						}}
						mutation_status={creation_status}
					/>
				</li>
			</ul>

			<h5>Default Status options available to all LeadJig Users:</h5>
			<ul className="list-group mb-3">
				{global_default_statuses?.map((status) => (
					<li key={status.id} className="list-group-item">
						{status.text}
						{status.default_for_new_contacts && (
							<div className="float-right">(default for new contacts)</div>
						)}
					</li>
				))}
			</ul>

			<ul className="pl-4">
				<li>Custom Status options can only be deleted if they are not in use.</li>
				<li>Advisor-level Users may also create their own Custom Statuses within their Preferences.</li>
			</ul>
		</div>
	)
}

ContactClientStatuses.propTypes = {
	organization_id: PropTypes.string,
}

export default memo(ContactClientStatuses)
